import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ESG_ICONS } from '../../constants/esgIcons';
import { blueRamp } from '../../theme/chartTheme';
import type { CarbonAvoidedByStream } from '../../types/esg-dashboard.type';
import { carbonMaterialLabel, formatEfVersions } from '../../utils/carbonAvoidance.util';
import ChartCard from './ChartCard';

interface CarbonAvoidedByStreamChartProps {
  streams: CarbonAvoidedByStream[];
  title: string;
  chartType: 'bar' | 'line' | 'area' | 'pie';
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  hideMenu?: boolean;
  fillHeight?: boolean;
  horizontalBar?: boolean;
  caption?: React.ReactNode;
  id?: string;
}

/**
 * Avoided emissions by catalog material, with emission-factor versions. Ranked
 * largest first and coloured with one blue at decreasing strength.
 */
const CarbonAvoidedByStreamChart: React.FC<CarbonAvoidedByStreamChartProps> = ({
  streams,
  title,
  chartType,
  onMenuClick,
  hideMenu = false,
  fillHeight = false,
  horizontalBar = false,
  caption,
  id,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const chartData = useMemo(
    () =>
      streams
        .map((row) => ({
          material_label: carbonMaterialLabel(row.material_label, t('carbonAvoidance.unclassified')),
          total_avoided_tco2e: row.total_avoided_tco2e,
          ef_label: formatEfVersions(row.emission_factor_versions ?? []),
        }))
        .sort((a, b) => (b.total_avoided_tco2e ?? 0) - (a.total_avoided_tco2e ?? 0)),
    [streams, t],
  );
  const colors = useMemo(() => blueRamp(theme, chartData.length), [theme, chartData.length]);

  return (
    <ChartCard
      id={id}
      title={title}
      icon={ESG_ICONS.avoidedEmissions}
      caption={caption}
      data={chartData}
      type={chartType}
      dataKey="total_avoided_tco2e"
      nameKey="material_label"
      metaKey="ef_label"
      valueKind="tco2e"
      unit={t('units.tco2e')}
      seriesLabel={t('carbonAvoidance.avoidedTco2e')}
      segmentColors={colors}
      onMenuClick={onMenuClick}
      hideMenu={hideMenu}
      fillPaper={fillHeight}
      barLayout={horizontalBar ? 'horizontal' : 'vertical'}
    />
  );
};

export default CarbonAvoidedByStreamChart;
